import React, { useState, useEffect, useCallback, useRef } from 'react';
import checkinService from '../services/checkinService';
import ClientFormScheduleModal from './ClientFormScheduleModal';
import CheckinFormView from './CheckinFormView';
import CheckinSharePointModal from './CheckinSharePointModal';
import {
  buildCheckinPdfBlob,
  defaultCheckinPdfFilename,
  exportCheckinFormToPdf,
} from './checkinPdfExport';

const TAMARAC_LAST_QUERY_KEY = 'checkin_tamarac_last_query';

function createEmptyFormData() {
  return {
    tamarac_contact_id: '',
    tamarac_household_id: '',
    household_name: '',
    birth_date: '',
    middle_name: '',
    portal_url: '',
    portal_status: '',
    preferred_delivery_method: '',
    portal_enabled: null,
    is_ucp_enabled: null,
    client_id: '',
    client_crm_id: '',
    default_account: '',
    custom_account: '',
    tamarac_raw_keys: [],
    prefill: null,
    name: '',
    email: '',
    phone: '',
    notes: '',
    secondary_advisor: '',
    client_type: 'hff',
    send_mode: 'now',
    scheduled_send_local: '',
  };
}

function resolveSecondaryAdvisorKey(advisorName) {
  const normalized = String(advisorName || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'bradford' || normalized.includes('brad')) return 'bradford';
  if (normalized === 'matt' || normalized.includes('matt')) return 'matt';
  return '';
}

function mergeSecondaryContactPrefill(prefill, details) {
  const nextPrefill = { ...(prefill && typeof prefill === 'object' ? prefill : {}) };

  const secondaryFirstName = String(details?.secondaryFirstName || '').trim();
  const secondaryLastName = String(details?.secondaryLastName || '').trim();
  const secondaryEmail = String(details?.secondaryEmail || '').trim();
  const secondaryAdvisorName = String(details?.secondaryAdvisorName || '').trim();
  const rebellionaire = !!details?.Rebellionaire;

  nextPrefill.secondary_advisor_name = secondaryAdvisorName;
  nextPrefill.secondary_first_name = secondaryFirstName;
  nextPrefill.secondary_last_name = secondaryLastName;
  nextPrefill.secondary_email = secondaryEmail;
  nextPrefill.rebellionaire = rebellionaire;

  if (secondaryFirstName || secondaryLastName || secondaryEmail) {
    nextPrefill.has_partner = 'yes';
    nextPrefill.partner_first_name = secondaryFirstName;
    nextPrefill.partner_last_name = secondaryLastName;
    nextPrefill.partner_email = secondaryEmail;
  }

  return nextPrefill;
}

function buildCheckinPrefill(picked) {
  const prefill = picked?.prefill && typeof picked.prefill === 'object' ? { ...picked.prefill } : {};
  const partnerName = [prefill.partner_first_name, prefill.partner_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    ...prefill,
    name: picked?.tamarac_display_name || prefill.name || '',
    email: picked?.email || prefill.email || '',
    phone: picked?.phone || prefill.phone || '',
    shares_finances: prefill.has_partner === 'yes' || partnerName ? 'yes' : '',
    partner_name: partnerName,
    rebellionaire: !!prefill.rebellionaire,
  };
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getMeetingState(client) {
  const rawStatus = String(client?.meeting_status || '').trim().toLowerCase();
  if (rawStatus === 'canceled' || rawStatus === 'cancelled') {
    return 'canceled';
  }
  if (rawStatus === 'scheduled' || client?.meeting_scheduled) {
    return 'scheduled';
  }
  return 'not_scheduled';
}

function formatMeetingTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/**
 * CheckinAdmin — advisor-facing client management for the Annual Check-In
 * wellness form.
 *
 * Renders inside the unified ClientForms page as one of the form-type
 * tabs. Mirrors the cashflow/FPR admin layout (search, filter, table,
 * detail panel) but uses the Annual Check-In backend routes.
 */
const CheckinAdmin = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(createEmptyFormData());
  const [tamaracQuery, setTamaracQuery] = useState('');
  const [tamaracOptions, setTamaracOptions] = useState([]);
  const [tamaracLoading, setTamaracLoading] = useState(false);
  const [showTamaracOptions, setShowTamaracOptions] = useState(false);
  const tamaracRequestSeqRef = useRef(0);
  const tamaracJustPickedRef = useRef(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [linkClient, setLinkClient] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleClient, setScheduleClient] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [meetingFilter, setMeetingFilter] = useState('all');
  const [advisorFilter, setAdvisorFilter] = useState('all');
  const [showProcessed, setShowProcessed] = useState(false);

  const [detailClient, setDetailClient] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishClient, setPublishClient] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [emailingId, setEmailingId] = useState(null);

  useEffect(() => {
    if (tamaracJustPickedRef.current) {
      tamaracJustPickedRef.current = false;
      return undefined;
    }

    const query = tamaracQuery.trim();
    if (!showCreateModal) return undefined;

    if (query.length < 2) {
      setTamaracOptions([]);
      setShowTamaracOptions(false);
      return undefined;
    }

    let active = true;
    const requestId = ++tamaracRequestSeqRef.current;

    const timer = setTimeout(() => {
      setTamaracLoading(true);
      setShowTamaracOptions(true);

      checkinService.searchTamaracClientsPA(query, 25)
        .then((results) => {
          if (!active || requestId !== tamaracRequestSeqRef.current) return;
          setTamaracOptions(Array.isArray(results) ? results : []);
        })
        .catch(() => {
          if (!active || requestId !== tamaracRequestSeqRef.current) return;
          setTamaracOptions([]);
        })
        .finally(() => {
          if (active && requestId === tamaracRequestSeqRef.current) {
            setTamaracLoading(false);
          }
        });
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [showCreateModal, tamaracQuery]);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await checkinService.fetchAllClients();
      setClients(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.secondary_advisor) {
      setError('Please select the advisor (Bradford or Matt).');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const result = await checkinService.createClient({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        notes: formData.notes || null,
        knowledgebase_client_id: formData.tamarac_contact_id || null,
        prefill: {
          ...(formData.prefill || {}),
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          rebellionaire: formData.client_type === 'rebellionaire',
        },
        secondary_advisor: formData.secondary_advisor,
        client_type: formData.client_type || 'hff',
        send_mode: formData.send_mode || 'now',
        scheduled_send_at: formData.scheduled_send_local
          ? new Date(formData.scheduled_send_local).toISOString()
          : null,
      });
      if (result.delivery_status === 'scheduled') {
        setSuccess(`Client created. Initial send scheduled for ${new Date(result.scheduled_send_at).toLocaleString()}.`);
      } else {
        setSuccess(`Client created. Magic link: ${result.magic_link}`);
      }
      setShowCreateModal(false);
      setFormData(createEmptyFormData());
      setTamaracQuery('');
      setTamaracOptions([]);
      setShowTamaracOptions(false);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePickTamaracClient = (contactId) => {
    const picked = tamaracOptions.find((opt) => opt.tamarac_contact_id === contactId);
    if (!picked) {
      setFormData(createEmptyFormData());
      return;
    }

    const nextPrefill = buildCheckinPrefill(picked);
    setFormData((prev) => ({
      ...prev,
      tamarac_contact_id: picked.tamarac_contact_id,
      tamarac_household_id: picked.tamarac_household_id || '',
      household_name: picked.household_name || '',
      birth_date: picked.birth_date || '',
      middle_name: picked.middle_name || '',
      portal_url: picked.portal_url || '',
      portal_status: picked.portal_status || '',
      preferred_delivery_method: picked.preferred_delivery_method || '',
      portal_enabled: typeof picked.portal_enabled === 'boolean' ? picked.portal_enabled : null,
      is_ucp_enabled: typeof picked.is_ucp_enabled === 'boolean' ? picked.is_ucp_enabled : null,
      client_id: picked.client_id || '',
      client_crm_id: picked.client_crm_id || '',
      default_account: picked.default_account || '',
      custom_account: picked.custom_account || '',
      tamarac_raw_keys: Array.isArray(picked.tamarac_raw_keys) ? picked.tamarac_raw_keys : [],
      prefill: nextPrefill,
      name: nextPrefill.name || '',
      email: nextPrefill.email || '',
      phone: nextPrefill.phone || '',
      secondary_advisor: '',
      client_type: nextPrefill.rebellionaire ? 'rebellionaire' : prev.client_type || 'hff',
    }));
    tamaracJustPickedRef.current = true;
    setTamaracQuery(
      `${picked.tamarac_display_name || 'Unknown Client'}${picked.email ? ` (${picked.email})` : ''}`,
    );
    if (picked.tamarac_display_name || picked.email) {
      localStorage.setItem(
        TAMARAC_LAST_QUERY_KEY,
        String(picked.tamarac_display_name || picked.email || '').trim(),
      );
    }
    setShowTamaracOptions(false);

    if (picked.source === 'pa') {
      const paPrefill = picked.prefill || {};
      const resolvedAdvisor = resolveSecondaryAdvisorKey(paPrefill.secondary_advisor_name);
      const mergedPrefill = mergeSecondaryContactPrefill(null, {
        secondaryAdvisorName: paPrefill.secondary_advisor_name || '',
        secondaryFirstName: paPrefill.secondary_first_name || '',
        secondaryLastName: paPrefill.secondary_last_name || '',
        secondaryEmail: paPrefill.secondary_email || '',
        Rebellionaire: paPrefill.rebellionaire || false,
      });

      setFormData((prev) => ({
        ...prev,
        prefill: { ...paPrefill, ...mergedPrefill },
        secondary_advisor: resolvedAdvisor || prev.secondary_advisor,
      }));
      return;
    }

    const detailsLookupId = picked.client_crm_id || picked.tamarac_contact_id;
    if (detailsLookupId) {
      setDetailsLoading(true);
      checkinService.fetchTamaracContactDetails(detailsLookupId)
        .then((details) => {
          const resolvedSecondaryAdvisor = resolveSecondaryAdvisorKey(details.secondaryAdvisorName);
          const mergedPrefill = mergeSecondaryContactPrefill(picked.prefill || null, details);
          setFormData((prev) => ({
            ...prev,
            prefill: mergedPrefill,
            secondary_advisor: resolvedSecondaryAdvisor || prev.secondary_advisor,
          }));
        })
        .catch((err) => {
          setError(err.response?.data?.detail || err.message);
        })
        .finally(() => {
          setDetailsLoading(false);
        });
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      setSubmitting(true);
      setError('');
      await checkinService.deleteClient(selectedClient.client_id);
      setSuccess(`Deleted client: ${selectedClient.name}`);
      setShowDeleteModal(false);
      setSelectedClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (client) => {
    try {
      setError('');
      const result = await checkinService.resendLink(client.client_id);
      setSuccess(`Magic link resent to ${client.name}: ${result.magic_link}`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleTogglePause = async (client) => {
    try {
      setError('');
      const pausing = !client.reminders_paused;
      await checkinService.patchClient(client.client_id, { reminders_paused: pausing });
      setSuccess(`Reminders ${pausing ? 'paused' : 'resumed'} for ${client.name}.`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleToggleProcessed = async (client) => {
    try {
      setError('');
      const nextProcessed = !client.processed;
      await checkinService.patchClient(client.client_id, { processed: nextProcessed });
      setSuccess(`${nextProcessed ? 'Marked' : 'Unmarked'} as processed: ${client.name}`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleGetLink = async (client) => {
    setLinkClient(client);
    setMagicLink('');
    setShowLinkModal(true);
    try {
      setLinkLoading(true);
      setError('');
      const result = await checkinService.getLink(client.client_id);
      setMagicLink(result.magic_link);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setShowLinkModal(false);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleExport = async (client) => {
    try {
      setError('');
      setExportingId(client.client_id);
      const data = await checkinService.getClientDetail(client.client_id);
      const clientFormData = data?.form?.form_data || {};
      exportCheckinFormToPdf(clientFormData, {
        clientName: client.name,
        clientEmail: client.email,
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'PDF export failed');
    } finally {
      setExportingId(null);
    }
  };

  const handlePublish = (client) => {
    setPublishClient(client);
    setShowPublishModal(true);
  };

  const handlePublishSuccess = (result) => {
    setShowPublishModal(false);
    setPublishClient(null);
    setSuccess(
      `Published to SharePoint: ${result.sharepoint_location || 'success'}`
      + (result.knowledgebase_status === 'added_to_knowledgebase'
        ? ' - added to knowledgebase.'
        : ''),
    );
  };

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const raw = String(reader.result || '');
      const base64 = raw.includes(',') ? raw.split(',')[1] : raw;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const handleEmailPdf = async (client) => {
    try {
      setError('');
      setEmailingId(client.client_id);

      const data = await checkinService.getClientDetail(client.client_id);
      const clientFormData = data?.form?.form_data || {};
      const pdfBlob = buildCheckinPdfBlob(clientFormData, {
        clientName: client.name,
        clientEmail: client.email,
      });
      const pdfBase64 = await blobToBase64(pdfBlob);
      const filename = defaultCheckinPdfFilename(client.name);

      const response = await checkinService.emailSummaryPdf(client.client_id, {
        filename,
        pdf_base64: pdfBase64,
      });

      const recipients = Array.isArray(response?.recipients) ? response.recipients.join(', ') : '';
      setSuccess(`IM review PDF emailed${recipients ? ` to ${recipients}` : ''}.`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Email PDF failed');
    } finally {
      setEmailingId(null);
    }
  };

  const handleOpenScheduleModal = (client) => {
    setScheduleClient(client);
    setShowScheduleModal(true);
  };

  const handleScheduleSave = async (payload) => {
    if (!scheduleClient) return;
    try {
      setSubmitting(true);
      setError('');
      await checkinService.scheduleSend(scheduleClient.client_id, payload);
      setSuccess(`Scheduled send saved for ${scheduleClient.name}.`);
      setShowScheduleModal(false);
      setScheduleClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleCancel = async () => {
    if (!scheduleClient) return;
    try {
      setSubmitting(true);
      setError('');
      await checkinService.cancelScheduledSend(scheduleClient.client_id);
      setSuccess(`Canceled scheduled send for ${scheduleClient.name}.`);
      setShowScheduleModal(false);
      setScheduleClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, clientId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(clientId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewClient = async (client) => {
    if (detailClient?.client_id === client.client_id) {
      setDetailClient(null);
      setDetailForm(null);
      return;
    }
    try {
      setDetailLoading(true);
      setDetailClient(client);
      setDetailForm(null);
      const data = await checkinService.getClientDetail(client.client_id);
      setDetailForm(data.form?.form_data || {});
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setDetailClient(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredClients = clients.filter((c) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term);
    if (!matchesSearch) return false;

    if (!showProcessed && c.processed) return false;

    const isComplete = !!c.completed || (c.total_sections > 0 && c.sections_completed === c.total_sections);
    const statusMatches = statusFilter === 'all'
      || (statusFilter === 'completed' && isComplete)
      || (statusFilter === 'in_progress' && !isComplete && (c.sections_completed || 0) > 0)
      || (statusFilter === 'not_started' && !isComplete && (c.sections_completed || 0) === 0);

    const meetingState = getMeetingState(c);
    const meetingMatches = meetingFilter === 'all' || meetingState === meetingFilter;

    const advisorKey = (c.secondary_advisor || '').toLowerCase();
    const advisorMatches = advisorFilter === 'all' || advisorKey === advisorFilter;

    return statusMatches && meetingMatches && advisorMatches;
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted small">
          <i className="bi bi-info-circle me-1" />
          Send the IM Update form to clients and track their progress.
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-person-check me-1" /> Select Tamarac Client
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <div style={{ wordBreak: 'break-all' }}>{success}</div>
          {success.includes('http') && (
            <button
              className="btn btn-sm btn-outline-success mt-2"
              onClick={() => {
                const url = success.match(/(https?:\/\/\S+)/)?.[1];
                if (url) copyToClipboard(url, 'success');
              }}
            >
              <i className="bi bi-clipboard me-1" />
              {copiedId === 'success' ? 'Copied!' : 'Copy Link'}
            </button>
          )}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      <div className="mb-3 d-flex gap-2 flex-wrap align-items-center">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '320px' }}
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="btn-group">
          {[
            { key: 'all', label: 'All' },
            { key: 'completed', label: 'Completed' },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'not_started', label: 'Not Started' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${statusFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="btn-group">
          {[
            { key: 'all', label: 'Any Meeting' },
            { key: 'scheduled', label: 'Scheduled' },
            { key: 'canceled', label: 'Canceled' },
            { key: 'not_scheduled', label: 'Not Scheduled' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${meetingFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setMeetingFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="btn-group">
          {[
            { key: 'all', label: 'Any Advisor' },
            { key: 'bradford', label: 'Bradford' },
            { key: 'matt', label: 'Matt' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${advisorFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setAdvisorFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`btn btn-sm ${showProcessed ? 'btn-warning' : 'btn-outline-secondary'}`}
          onClick={() => setShowProcessed((v) => !v)}
          title={showProcessed ? 'Showing processed workflows' : 'Hiding processed workflows'}
        >
          <i className={`bi ${showProcessed ? 'bi-eye' : 'bi-eye-slash'} me-1`} />
          {showProcessed ? 'Hide Processed' : 'Show Processed'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox display-4 d-block mb-2" />
          {searchTerm || statusFilter !== 'all' || meetingFilter !== 'all' || advisorFilter !== 'all' || !showProcessed
            ? 'No clients match your filters.'
            : 'No clients yet. Click "Select Tamarac Client" to create one.'}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Advisor</th>
                <th>Meeting</th>
                <th>Reminders</th>
                <th>Next Send</th>
                <th>Device</th>
                <th>Created</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => {
                const filled = client.sections_completed || 0;
                const totalSections = client.total_sections || 7;
                const isComplete = client.completed || (totalSections > 0 && filled === totalSections);
                const pct = isComplete ? 100 : Math.round((filled / totalSections) * 100);
                const color = isComplete ? 'bg-success' : pct > 0 ? 'bg-info' : 'bg-secondary';
                const meetingState = getMeetingState(client);
                return (
                  <tr
                    key={client.client_id}
                    onClick={() => handleViewClient(client)}
                    style={{ cursor: 'pointer' }}
                    className={detailClient?.client_id === client.client_id ? 'table-active' : ''}
                  >
                    <td className="fw-semibold">{client.name}</td>
                    <td>{client.email}</td>
                    <td style={{ minWidth: '140px' }}>
                      <div className="progress" style={{ height: '18px' }}>
                        <div
                          className={`progress-bar ${color}`}
                          role="progressbar"
                          style={{ width: `${Math.max(pct, pct > 0 ? 10 : 0)}%` }}
                        >
                          {pct > 0 ? `${pct}%` : ''}
                        </div>
                      </div>
                      <small className="text-muted">
                        {client.processed
                          ? 'Processed'
                          : isComplete
                            ? 'Completed'
                            : pct === 0
                              ? 'Not Started'
                              : `${filled} of ${totalSections} sections`}
                      </small>
                    </td>
                    <td>
                      {client.secondary_advisor_label
                        || (client.secondary_advisor
                          ? client.secondary_advisor.charAt(0).toUpperCase() + client.secondary_advisor.slice(1)
                          : <span className="text-muted">—</span>)}
                    </td>
                    <td>
                      {meetingState === 'scheduled' ? (
                        <span className="text-success">
                          <i className="bi bi-calendar2-check me-1" />
                          {client.meeting_scheduled_at
                            ? formatMeetingTime(client.meeting_scheduled_at)
                            : 'Scheduled'}
                        </span>
                      ) : meetingState === 'canceled' ? (
                        <span className="text-warning">
                          <i className="bi bi-calendar2-x me-1" />
                          {client.meeting_scheduled_at
                            ? `Canceled: ${formatMeetingTime(client.meeting_scheduled_at)}`
                            : 'Canceled'}
                        </span>
                      ) : (
                        <span className="text-muted">
                          <i className="bi bi-calendar2-x me-1" />
                          Not scheduled
                        </span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        if (client.processed) return <span className="text-muted">—</span>;
                        if (isComplete) return <span className="text-muted">—</span>;
                        if (client.reminders_paused) {
                          return (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-pause-circle me-1" />Paused
                            </span>
                          );
                        }
                        const ref = client.last_reminder_sent || client.created_at;
                        if (!ref) return <span className="text-muted">—</span>;
                        const daysSince = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
                        const interval = 10;
                        const daysLeft = interval - daysSince;
                        if (daysLeft <= 0) {
                          return (
                            <span className="badge bg-danger">
                              <i className="bi bi-envelope me-1" />Due now
                            </span>
                          );
                        }
                        return <span className="text-muted small">{daysLeft}d until next</span>;
                      })()}
                    </td>
                    <td>
                      {client.next_scheduled_send_at ? (
                        <div>
                          <div className="small fw-semibold">{formatDate(client.next_scheduled_send_at)}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {client.next_scheduled_send_reason || 'scheduled'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {client.device_type ? (
                        <span title={client.device_type}>
                          <i className={`bi ${client.device_type === 'mobile' ? 'bi-phone' : 'bi-laptop'}`} />
                          {' '}{client.device_type === 'mobile' ? 'Mobile' : 'Desktop'}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{formatDate(client.created_at)}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm" onClick={(e) => e.stopPropagation()}>
                        <button className="btn btn-outline-secondary" title="Get magic link" onClick={() => handleGetLink(client)}>
                          <i className="bi bi-link-45deg" />
                        </button>
                        <button className="btn btn-outline-primary" title="Resend magic link via email" onClick={() => handleResend(client)}>
                          <i className="bi bi-send" />
                        </button>
                        <button
                          className="btn btn-outline-success"
                          title="Export IM review PDF"
                          onClick={() => handleExport(client)}
                          disabled={exportingId === client.client_id}
                        >
                          {exportingId === client.client_id ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <i className="bi bi-file-earmark-pdf" />
                          )}
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          title="Upload IM review PDF to SharePoint"
                          onClick={() => handlePublish(client)}
                        >
                          <i className="bi bi-cloud-upload" />
                        </button>
                        <button
                          className="btn btn-outline-info"
                          title="Email IM review PDF"
                          onClick={() => handleEmailPdf(client)}
                          disabled={emailingId === client.client_id}
                        >
                          {emailingId === client.client_id ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <i className="bi bi-envelope" />
                          )}
                        </button>
                        <button
                          className={`btn ${client.reminders_paused ? 'btn-warning' : 'btn-outline-secondary'}`}
                          title={client.reminders_paused ? 'Resume reminder emails' : 'Pause reminder emails'}
                          onClick={() => handleTogglePause(client)}
                        >
                          <i className={`bi ${client.reminders_paused ? 'bi-play-circle' : 'bi-pause-circle'} me-1`} />
                          {client.reminders_paused ? 'Resume' : 'Pause'}
                        </button>
                        <button
                          className="btn btn-outline-info"
                          title="Schedule next form send"
                          onClick={() => handleOpenScheduleModal(client)}
                        >
                          <i className="bi bi-calendar-plus" />
                        </button>
                        <button
                          className={`btn ${client.processed ? 'btn-warning' : 'btn-outline-warning'}`}
                          title={client.processed ? 'Mark as unprocessed' : 'Mark as processed'}
                          onClick={() => handleToggleProcessed(client)}
                        >
                          <i className={`bi ${client.processed ? 'bi-arrow-counterclockwise' : 'bi-check2-circle'}`} />
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          title="Delete client"
                          onClick={() => { setSelectedClient(client); setShowDeleteModal(true); }}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Client Detail Panel */}
      {detailClient && (
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-person-lines-fill me-2" />
              {detailClient.name}
              <small className="text-muted ms-2">({detailClient.email})</small>
            </h5>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-success"
                title="Export IM review PDF"
                onClick={() => handleExport(detailClient)}
                disabled={exportingId === detailClient.client_id}
              >
                {exportingId === detailClient.client_id ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <i className="bi bi-file-earmark-pdf me-1" />
                    Export PDF
                  </>
                )}
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                title="Upload IM review PDF to SharePoint"
                onClick={() => handlePublish(detailClient)}
              >
                <i className="bi bi-cloud-upload me-1" />
                Upload to SharePoint
              </button>
              <button
                className="btn btn-sm btn-outline-info"
                title="Email IM review PDF"
                onClick={() => handleEmailPdf(detailClient)}
                disabled={emailingId === detailClient.client_id}
              >
                {emailingId === detailClient.client_id ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <i className="bi bi-envelope me-1" />
                    Email PDF
                  </>
                )}
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setDetailClient(null); setDetailForm(null); }}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
          </div>
          <div className="card-body">
            {detailLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <CheckinFormView formData={detailForm} />
            )}
          </div>
        </div>
      )}

      {showPublishModal && publishClient && (
        <CheckinSharePointModal
          client={publishClient}
          onClose={() => {
            setShowPublishModal(false);
            setPublishClient(null);
          }}
          onSuccess={handlePublishSuccess}
        />
      )}

      {/* Create Client Modal */}
      {showCreateModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create IM Update Form for Client</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Search Tamarac Client <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type name or email..."
                        autoComplete="off"
                        value={tamaracQuery}
                        onFocus={() => {
                          if (tamaracOptions.length) setShowTamaracOptions(true);
                        }}
                        onChange={(e) => {
                          setTamaracQuery(e.target.value);
                          setFormData(createEmptyFormData());
                        }}
                      />
                      {tamaracLoading && (
                        <span className="position-absolute top-50 end-0 translate-middle-y me-3" aria-hidden="true" title="Searching…">
                          <span className="spinner-border spinner-border-sm text-secondary" role="status" />
                        </span>
                      )}
                      {showTamaracOptions && tamaracQuery.trim().length >= 2 && (
                        <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 20, maxHeight: '220px', overflowY: 'auto' }}>
                          {!tamaracLoading && tamaracOptions.length === 0 && (
                            <div className="list-group-item text-muted small">No clients found.</div>
                          )}
                          {tamaracOptions.map((opt) => (
                            <button
                              key={`${opt.tamarac_contact_id}-${opt.email || ''}`}
                              type="button"
                              className="list-group-item list-group-item-action"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handlePickTamaracClient(opt.tamarac_contact_id);
                              }}
                            >
                              <div className="fw-semibold">{opt.tamarac_display_name || 'Unknown Client'}</div>
                              <small className="text-muted">{opt.email || 'No email'}{opt.tamarac_household_id ? ` • Household ${opt.tamarac_household_id}` : ''}</small>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-text">
                      Use at least 2 characters and pick a client from the suggestions.
                    </div>
                  </div>

                  {(formData.tamarac_household_id || formData.household_name) && (
                    <div className="mb-3 form-text">
                      {formData.tamarac_household_id ? `Household ID: ${formData.tamarac_household_id}` : ''}
                      {formData.tamarac_household_id && formData.household_name ? ' • ' : ''}
                      {formData.household_name ? `Household: ${formData.household_name}` : ''}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      readOnly={!!formData.tamarac_contact_id}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      className="form-control"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="+1-555-555-0123"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Send Timing</label>
                    <div className="d-flex gap-3 mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="send_mode"
                          id="checkin-send-now"
                          value="now"
                          checked={formData.send_mode === 'now'}
                          onChange={(e) => setFormData({ ...formData, send_mode: e.target.value })}
                        />
                        <label className="form-check-label" htmlFor="checkin-send-now">Send now</label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="send_mode"
                          id="checkin-send-scheduled"
                          value="scheduled"
                          checked={formData.send_mode === 'scheduled'}
                          onChange={(e) => setFormData({ ...formData, send_mode: e.target.value })}
                        />
                        <label className="form-check-label" htmlFor="checkin-send-scheduled">Schedule send</label>
                      </div>
                    </div>
                    {formData.send_mode === 'scheduled' && (
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.scheduled_send_local || ''}
                        onChange={(e) => setFormData({ ...formData, scheduled_send_local: e.target.value })}
                        required
                      />
                    )}
                    <div className="form-text">Choose "Schedule send" to send the magic link later.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Advisor <span className="text-danger">*</span>
                      {detailsLoading && (
                        <span
                          className="spinner-border spinner-border-sm ms-2 text-secondary"
                          role="status"
                          aria-label="Loading advisor info"
                        />
                      )}
                    </label>
                    <div className="d-flex gap-3">
                      {[
                        { key: 'bradford', label: 'Bradford' },
                        { key: 'matt', label: 'Matt' },
                      ].map((opt) => (
                        <div className="form-check" key={opt.key}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="secondary_advisor"
                            id={`checkin-advisor-${opt.key}`}
                            value={opt.key}
                            checked={formData.secondary_advisor === opt.key}
                            onChange={(e) =>
                              setFormData({ ...formData, secondary_advisor: e.target.value })
                            }
                          />
                          <label className="form-check-label" htmlFor={`checkin-advisor-${opt.key}`}>
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="form-text">
                      This controls which OnceHub scheduling link is shown in the wizard.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Client Theme</label>
                    <select
                      className="form-select"
                      value={formData.client_type}
                      onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                    >
                      <option value="hff">HFF</option>
                      <option value="rebellionaire">Rebellionaire</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting || detailsLoading || (formData.send_mode === 'scheduled' && !formData.scheduled_send_local)}
                  >
                    {submitting ? 'Creating…' : (formData.send_mode === 'scheduled' ? 'Create & Schedule Send' : 'Create & Send Link')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Client</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete <strong>{selectedClient.name}</strong>? This cannot be undone.
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
                  {submitting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Magic Link Modal */}
      {showLinkModal && linkClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Magic Link — {linkClient.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowLinkModal(false)}></button>
              </div>
              <div className="modal-body">
                {linkLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="input-group">
                    <input type="text" className="form-control" readOnly value={magicLink} />
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => copyToClipboard(magicLink, linkClient.client_id)}
                    >
                      <i className="bi bi-clipboard me-1" />
                      {copiedId === linkClient.client_id ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowLinkModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ClientFormScheduleModal
        isOpen={showScheduleModal}
        title="Schedule Next IM Update Request"
        client={scheduleClient}
        schedule={scheduleClient ? {
          scheduled_send_at: scheduleClient.next_scheduled_send_at,
          send_reason: scheduleClient.next_scheduled_send_reason,
          send_email: scheduleClient.next_scheduled_send_email,
          reset_progress_on_send: true,
        } : null}
        submitting={submitting}
        onClose={() => { setShowScheduleModal(false); setScheduleClient(null); }}
        onSave={handleScheduleSave}
        onCancelSchedule={handleScheduleCancel}
        reasonOptions={[
          { value: 'next_year', label: 'Next Year Update' },
          { value: 'next_quarter', label: 'Next Quarter Update' },
          { value: 'six_month', label: '6 Month Update' },
          { value: 'custom', label: 'Custom' },
        ]}
        defaultReason="next_year"
      />
    </div>
  );
};

export default CheckinAdmin;
